import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from './firebase';

/** Days before soft-deleted items are permanently removed. */
export const TRASH_RETENTION_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const BATCH_LIMIT = 500;

const CASE_CHILD_COLLECTIONS: {
  name: string;
  type: TrashEntityType;
  titleFn: (d: Record<string, unknown>) => string;
  useLawyerFilter?: boolean;
  useUserFilter?: boolean;
}[] = [
  {
    name: 'proceedings',
    type: 'proceeding',
    titleFn: (d) => String(d.description || d.stage || 'Proceeding'),
    useLawyerFilter: true,
  },
  {
    name: 'notes',
    type: 'note',
    titleFn: (d) =>
      String(typeof d.content === 'string' ? d.content.slice(0, 80) : 'Note'),
    useLawyerFilter: true,
  },
  {
    name: 'documents',
    type: 'document',
    titleFn: (d) => String(d.title || d.name || 'Document'),
    useLawyerFilter: true,
  },
  {
    name: 'tasks',
    type: 'task',
    titleFn: (d) => String(d.title || 'Task'),
    useLawyerFilter: true,
  },
  {
    name: 'reminders',
    type: 'reminder',
    titleFn: (d) => String(d.message || 'Reminder'),
    useUserFilter: true,
  },
];

export type TrashEntityType =
  | 'case'
  | 'note'
  | 'proceeding'
  | 'document'
  | 'draft'
  | 'template'
  | 'task'
  | 'reminder'
  | 'organization';

export interface TrashItem {
  id: string;
  entityType: TrashEntityType;
  collection: string;
  title: string;
  subtitle?: string;
  deletedAt: Date;
  purgeAfter: Date;
  deletedBy?: string;
  trashBatchId?: string;
  caseId?: string;
}

const TRASH_COLLECTIONS: {
  col: string;
  defaultType: TrashEntityType;
  title: (data: Record<string, unknown>) => string;
  subtitle?: (data: Record<string, unknown>) => string | undefined;
  resolveType?: (data: Record<string, unknown>) => TrashEntityType;
}[] = [
  {
    col: 'cases',
    defaultType: 'case',
    title: (d) => String(d.trashTitle || d.title || 'Case'),
    subtitle: (d) => (d.caseNumber ? String(d.caseNumber) : undefined),
  },
  {
    col: 'notes',
    defaultType: 'note',
    title: (d) =>
      String(d.trashTitle || (typeof d.content === 'string' ? d.content.slice(0, 80) : '') || 'Note'),
  },
  {
    col: 'proceedings',
    defaultType: 'proceeding',
    title: (d) =>
      String(d.trashTitle || d.description || d.stage || 'Proceeding'),
    subtitle: (d) => (d.date ? String(d.date) : undefined),
  },
  {
    col: 'documents',
    defaultType: 'document',
    title: (d) => String(d.trashTitle || d.title || d.name || 'Document'),
    resolveType: (d) => {
      if (d.type === 'draft') return 'draft';
      if (d.type === 'template') return 'template';
      return 'document';
    },
  },
  {
    col: 'tasks',
    defaultType: 'task',
    title: (d) => String(d.trashTitle || d.title || 'Task'),
  },
  {
    col: 'reminders',
    defaultType: 'reminder',
    title: (d) => String(d.trashTitle || d.message || 'Reminder'),
  },
  {
    col: 'organizations',
    defaultType: 'organization',
    title: (d) => String(d.trashTitle || d.name || 'Organization'),
  },
];

export function isDeleted(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;
  return data.deletedAt != null && data.deletedAt !== '';
}

export function filterActive<T extends { deletedAt?: unknown }>(items: T[]): T[] {
  return items.filter((item) => !isDeleted(item as Record<string, unknown>));
}

function wsCollection(workspaceId: string, col: string) {
  return collection(db, 'workspaces', workspaceId, col);
}

function wsDocRef(workspaceId: string, col: string, id: string) {
  return doc(db, 'workspaces', workspaceId, col, id);
}

function parseFirestoreDate(value: unknown): Date {
  if (!value) return new Date(0);
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
}

export function getPurgeAfterDate(
  data: Record<string, unknown>,
  deletedAt: Date,
): Date {
  if (data.purgeAfter != null && data.purgeAfter !== '') {
    return parseFirestoreDate(data.purgeAfter);
  }
  return new Date(deletedAt.getTime() + TRASH_RETENTION_DAYS * MS_PER_DAY);
}

export function isTrashItemExpired(item: Pick<TrashItem, 'purgeAfter'>): boolean {
  return item.purgeAfter.getTime() <= Date.now();
}

export function getDaysUntilPurge(purgeAfter: Date): number {
  const ms = purgeAfter.getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / MS_PER_DAY);
}

function softDeleteFields(
  options: {
    deletedBy: string;
    entityType: TrashEntityType;
    title?: string;
    trashBatchId?: string;
    deletedAt?: Date;
  },
): Record<string, string> {
  const deletedAt = options.deletedAt ?? new Date();
  const purgeAfter = new Date(
    deletedAt.getTime() + TRASH_RETENTION_DAYS * MS_PER_DAY,
  );
  return {
    deletedAt: deletedAt.toISOString(),
    purgeAfter: purgeAfter.toISOString(),
    deletedBy: options.deletedBy,
    trashEntityType: options.entityType,
    ...(options.title ? { trashTitle: options.title } : {}),
    ...(options.trashBatchId ? { trashBatchId: options.trashBatchId } : {}),
  };
}

export async function softDelete(
  ref: DocumentReference,
  options: {
    deletedBy: string;
    entityType: TrashEntityType;
    title?: string;
    trashBatchId?: string;
  },
): Promise<void> {
  await updateDoc(ref, softDeleteFields(options));
}

async function commitSoftDeleteBatch(
  updates: { ref: DocumentReference; fields: Record<string, string> }[],
): Promise<void> {
  if (updates.length === 0) return;

  let batch = writeBatch(db);
  let count = 0;

  const flush = async () => {
    if (count === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    count = 0;
  };

  for (const { ref, fields } of updates) {
    batch.update(ref, fields);
    count += 1;
    if (count >= BATCH_LIMIT) {
      await flush();
    }
  }

  await flush();
}

function restoreFields(): Record<string, ReturnType<typeof deleteField>> {
  return {
    deletedAt: deleteField(),
    deletedBy: deleteField(),
    trashEntityType: deleteField(),
    trashTitle: deleteField(),
    trashBatchId: deleteField(),
    purgeAfter: deleteField(),
  };
}

export async function restoreDoc(ref: DocumentReference): Promise<void> {
  await updateDoc(ref, restoreFields());
}

async function commitRestoreBatch(refs: DocumentReference[]): Promise<void> {
  if (refs.length === 0) return;

  const fields = restoreFields();
  let batch = writeBatch(db);
  let count = 0;

  const flush = async () => {
    if (count === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    count = 0;
  };

  for (const ref of refs) {
    batch.update(ref, fields);
    count += 1;
    if (count >= BATCH_LIMIT) {
      await flush();
    }
  }

  await flush();
}

async function commitDeleteBatch(refs: DocumentReference[]): Promise<void> {
  if (refs.length === 0) return;

  let batch = writeBatch(db);
  let count = 0;

  const flush = async () => {
    if (count === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    count = 0;
  };

  for (const ref of refs) {
    batch.delete(ref);
    count += 1;
    if (count >= BATCH_LIMIT) {
      await flush();
    }
  }

  await flush();
}

function matchesTrashBatchChild(
  data: Record<string, unknown>,
  trashBatchId?: string,
): boolean {
  if (!isDeleted(data)) return false;
  if (trashBatchId && data.trashBatchId !== trashBatchId) return false;
  return true;
}

async function queryCaseChildDocs(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
): Promise<DocumentReference[]> {
  const snapshots = await Promise.all(
    CASE_CHILD_COLLECTIONS.map((cfg) =>
      getDocs(
        query(wsCollection(workspaceId, cfg.name), where('caseId', '==', caseId)),
      ),
    ),
  );

  const refs: DocumentReference[] = [];
  for (const snap of snapshots) {
    for (const docSnap of snap.docs) {
      const data = docSnap.data() as Record<string, unknown>;
      if (matchesTrashBatchChild(data, trashBatchId)) {
        refs.push(docSnap.ref);
      }
    }
  }
  return refs;
}

export async function permanentDelete(ref: DocumentReference): Promise<void> {
  await deleteDoc(ref);
}

export interface CaseDeleteFilters {
  workspaceId?: string;
  lawyerId?: string;
  userId?: string;
}

function buildCaseChildQueryConstraints(
  caseId: string,
  cfg: (typeof CASE_CHILD_COLLECTIONS)[number],
  filters?: CaseDeleteFilters,
) {
  const constraints = [where('caseId', '==', caseId)];
  if (filters?.workspaceId) {
    constraints.push(where('workspaceId', '==', filters.workspaceId));
  }
  if (filters?.lawyerId && cfg.useLawyerFilter) {
    constraints.push(where('lawyerId', '==', filters.lawyerId));
  }
  if (filters?.userId && cfg.useUserFilter) {
    constraints.push(where('userId', '==', filters.userId));
  }
  return constraints;
}

export async function softDeleteCaseDoc(
  workspaceId: string,
  caseId: string,
  deletedBy: string,
  batchId: string,
  caseTitle?: string,
): Promise<void> {
  let title = caseTitle;
  if (!title) {
    const caseSnap = await getDocs(
      query(wsCollection(workspaceId, 'cases'), where('__name__', '==', caseId)),
    );
    title = String(caseSnap.docs[0]?.data()?.title || 'Case');
  }

  await softDelete(wsDocRef(workspaceId, 'cases', caseId), {
    deletedBy,
    entityType: 'case',
    title,
    trashBatchId: batchId,
  });
}

export async function softDeleteCaseChildren(
  workspaceId: string,
  caseId: string,
  deletedBy: string,
  batchId: string,
  filters?: CaseDeleteFilters,
): Promise<void> {
  const deletedAt = new Date();
  const snapshots = await Promise.all(
    CASE_CHILD_COLLECTIONS.map(async (cfg) => {
      const snap = await getDocs(
        query(
          wsCollection(workspaceId, cfg.name),
          ...buildCaseChildQueryConstraints(caseId, cfg, filters),
        ),
      );
      return { cfg, snap };
    }),
  );

  const updates: { ref: DocumentReference; fields: Record<string, string> }[] = [];
  for (const { cfg, snap } of snapshots) {
    for (const docSnap of snap.docs) {
      updates.push({
        ref: docSnap.ref,
        fields: softDeleteFields({
          deletedBy,
          entityType: cfg.type,
          title: cfg.titleFn(docSnap.data() as Record<string, unknown>),
          trashBatchId: batchId,
          deletedAt,
        }),
      });
    }
  }

  await commitSoftDeleteBatch(updates);
}

export async function softDeleteCaseCascade(
  workspaceId: string,
  caseId: string,
  deletedBy: string,
  filters?: CaseDeleteFilters,
): Promise<string> {
  const batchId = `batch_${Date.now()}_${caseId}`;
  await softDeleteCaseDoc(workspaceId, caseId, deletedBy, batchId);
  await softDeleteCaseChildren(workspaceId, caseId, deletedBy, batchId, filters);
  return batchId;
}

/** Soft-delete case doc immediately, then cascade children in the background. */
export async function softDeleteCaseInBackground(
  workspaceId: string,
  caseId: string,
  deletedBy: string,
  filters?: CaseDeleteFilters,
  onChildError?: (err: unknown) => void,
): Promise<string> {
  const batchId = `batch_${Date.now()}_${caseId}`;
  await softDeleteCaseDoc(workspaceId, caseId, deletedBy, batchId);
  void softDeleteCaseChildren(workspaceId, caseId, deletedBy, batchId, filters).catch(
    (err) => {
      console.error('Background case child deletion failed:', err);
      onChildError?.(err);
    },
  );
  return batchId;
}

export async function restoreCaseDoc(
  workspaceId: string,
  caseId: string,
): Promise<void> {
  await restoreDoc(wsDocRef(workspaceId, 'cases', caseId));
}

export async function restoreCaseChildren(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
): Promise<void> {
  const refs = await queryCaseChildDocs(workspaceId, caseId, trashBatchId);
  await commitRestoreBatch(refs);
}

export async function restoreCaseCascade(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
): Promise<void> {
  await restoreCaseDoc(workspaceId, caseId);
  await restoreCaseChildren(workspaceId, caseId, trashBatchId);
}

/** Restore case doc immediately, then cascade children in the background. */
export async function restoreCaseInBackground(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
  onChildError?: (err: unknown) => void,
): Promise<void> {
  await restoreCaseDoc(workspaceId, caseId);
  void restoreCaseChildren(workspaceId, caseId, trashBatchId).catch((err) => {
    console.error('Background case child restore failed:', err);
    onChildError?.(err);
  });
}

export async function permanentDeleteCaseChildren(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
): Promise<void> {
  const refs = await queryCaseChildDocs(workspaceId, caseId, trashBatchId);
  await commitDeleteBatch(refs);
}

export async function permanentDeleteCaseDoc(
  workspaceId: string,
  caseId: string,
): Promise<void> {
  await deleteDoc(wsDocRef(workspaceId, 'cases', caseId));
}

export async function permanentDeleteCaseCascade(
  workspaceId: string,
  caseId: string,
  trashBatchId?: string,
): Promise<void> {
  await permanentDeleteCaseChildren(workspaceId, caseId, trashBatchId);
  await permanentDeleteCaseDoc(workspaceId, caseId);
}

export function isTrashItemAffectedByAction(
  item: TrashItem,
  target: TrashItem,
): boolean {
  if (item.id === target.id && item.collection === target.collection) return true;
  if (
    target.entityType === 'case' &&
    target.trashBatchId &&
    item.trashBatchId === target.trashBatchId
  ) {
    return true;
  }
  return false;
}

export function getTrashItemsAffectedByAction(
  items: TrashItem[],
  target: TrashItem,
): TrashItem[] {
  return items.filter((i) => isTrashItemAffectedByAction(i, target));
}

export function filterTrashItemsAfterAction(
  items: TrashItem[],
  target: TrashItem,
): TrashItem[] {
  return items.filter((i) => !isTrashItemAffectedByAction(i, target));
}

export async function fetchTrashItems(workspaceId: string): Promise<TrashItem[]> {
  const items: TrashItem[] = [];

  const snapshots = await Promise.all(
    TRASH_COLLECTIONS.map((cfg) =>
      getDocs(query(wsCollection(workspaceId, cfg.col), limit(500))),
    ),
  );

  snapshots.forEach((snap, index) => {
    const cfg = TRASH_COLLECTIONS[index];
    snap.forEach((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;
      if (!isDeleted(data)) return;

      const entityType =
        (data.trashEntityType as TrashEntityType) ||
        cfg.resolveType?.(data) ||
        cfg.defaultType;

      const deletedAt = parseFirestoreDate(data.deletedAt);
      items.push({
        id: docSnap.id,
        entityType,
        collection: cfg.col,
        title: cfg.title(data),
        subtitle: cfg.subtitle?.(data),
        deletedAt,
        purgeAfter: getPurgeAfterDate(data, deletedAt),
        deletedBy: data.deletedBy ? String(data.deletedBy) : undefined,
        trashBatchId: data.trashBatchId ? String(data.trashBatchId) : undefined,
        caseId: data.caseId ? String(data.caseId) : undefined,
      });
    });
  });

  items.sort((a, b) => b.deletedAt.getTime() - a.deletedAt.getTime());
  return items;
}

export async function restoreTrashItem(
  workspaceId: string,
  item: TrashItem,
): Promise<void> {
  if (item.entityType === 'case') {
    await restoreCaseCascade(workspaceId, item.id, item.trashBatchId);
    return;
  }
  await restoreDoc(wsDocRef(workspaceId, item.collection, item.id));
}

export async function purgeTrashItem(
  workspaceId: string,
  item: TrashItem,
): Promise<void> {
  if (item.entityType === 'case') {
    await permanentDeleteCaseCascade(workspaceId, item.id, item.trashBatchId);
    return;
  }
  await permanentDelete(wsDocRef(workspaceId, item.collection, item.id));
}

/** Restore trash item; case children cascade in background. */
export async function restoreTrashItemInBackground(
  workspaceId: string,
  item: TrashItem,
  onChildError?: (err: unknown) => void,
): Promise<void> {
  if (item.entityType === 'case') {
    await restoreCaseInBackground(
      workspaceId,
      item.id,
      item.trashBatchId,
      onChildError,
    );
    return;
  }
  await restoreDoc(wsDocRef(workspaceId, item.collection, item.id));
}

/** Permanently delete trash item; case cascades run in background (returns promise). */
export function purgeTrashItemInBackground(
  workspaceId: string,
  item: TrashItem,
): Promise<void> {
  if (item.entityType === 'case') {
    return permanentDeleteCaseCascade(workspaceId, item.id, item.trashBatchId);
  }
  return permanentDelete(wsDocRef(workspaceId, item.collection, item.id));
}

/** Permanently delete trash items past their retention period. Returns count purged. */
export async function purgeExpiredTrash(workspaceId: string): Promise<number> {
  const items = await fetchTrashItems(workspaceId);
  const expired = items.filter(isTrashItemExpired);
  if (expired.length === 0) return 0;

  const purgedBatchIds = new Set<string>();
  const purgedCaseIds = new Set<string>();
  let count = 0;

  for (const item of expired.filter((i) => i.entityType === 'case')) {
    await purgeTrashItem(workspaceId, item);
    purgedCaseIds.add(item.id);
    if (item.trashBatchId) purgedBatchIds.add(item.trashBatchId);
    count += 1;
  }

  for (const item of expired.filter((i) => i.entityType !== 'case')) {
    if (item.trashBatchId && purgedBatchIds.has(item.trashBatchId)) continue;
    if (item.caseId && purgedCaseIds.has(item.caseId)) continue;
    await purgeTrashItem(workspaceId, item);
    count += 1;
  }

  return count;
}

export const TRASH_ENTITY_LABELS: Record<TrashEntityType, string> = {
  case: 'Case',
  note: 'Note',
  proceeding: 'Proceeding',
  document: 'Document',
  draft: 'Draft',
  template: 'Template',
  task: 'Task',
  reminder: 'Reminder',
  organization: 'Organization',
};
